import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { VerifyIdentityResponse, UserType } from '../../types/auth';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const SignupVerify: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user_type = (location.state as any)?.user_type as UserType || 'adult';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verified, setVerified] = useState(false);

  const handleVerify = async () => {
    setLoading(true);
    setError('');

    try {
      // Edge Function 호출: verify-identity-for-signup
      const { data, error: fnError } = await supabase.functions.invoke<VerifyIdentityResponse>(
        'verify-identity-for-signup',
        {
          body: { user_type }
        }
      );

      if (fnError) {
        console.error('Edge Function error:', fnError);
        const errorBody = (fnError as any).context?.body;

        // ReadableStream인 경우 읽기 시도
        if (errorBody instanceof ReadableStream) {
          try {
            const reader = errorBody.getReader();
            const { value } = await reader.read();
            const text = new TextDecoder().decode(value);
            const parsed = JSON.parse(text);
            if (parsed?.message) {
              setError(parsed.message + (parsed.debug ? `\n상세: ${parsed.debug}` : ''));
              return;
            }
          } catch (e) {
            console.error('Failed to parse error body:', e);
          }
        } else if (errorBody?.message) {
          setError(errorBody.message);
          return;
        }
        throw fnError;
      }

      if (!data?.success) {
        const errorMsg = data?.message || '본인인증에 실패했습니다.';
        const debugInfo = (data as any)?.debug ? `\n상세: ${(data as any).debug}` : '';
        setError(errorMsg + debugInfo);
        return;
      }

      // 성공 시 verified 상태로 변경 후 잠시 후 다음 단계로
      setVerified(true);

      setTimeout(() => {
        navigate('/signup/terms', {
          state: {
            token: data.token,
            preview: data.preview,
            user_type: user_type,
          }
        });
      }, 1000);

    } catch (err: any) {
      console.error('Verify error:', err);

      let errorMessage = '본인인증 중 오류가 발생했습니다.';
      if (err.message?.includes('Failed to send a request')) {
        errorMessage = 'Edge Function에 연결할 수 없습니다. 서버를 확인해주세요.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <div className="flex-1 bg-gray-50 py-12 mt-20">
        <div className="max-w-5xl mx-auto px-4">
        {/* Contents Head */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <h3 className="text-3xl font-bold text-gray-900">통합회원가입</h3>
            <div className="flex gap-4 text-sm">
              <Link to="/login" className="text-gray-600 hover:text-[#48c9a6]">로그인</Link>
              <Link to="/signup" className="text-[#48c9a6] font-medium">통합회원가입</Link>
            </div>
          </div>
          <div className="h-1 bg-[#48c9a6] w-20 mt-4"></div>
        </div>

        {/* Contents Body */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          {/* 본인인증 타이틀 */}
          <div className="flex items-center gap-3 mb-8 pb-4 border-b border-gray-200">
            <div className="w-10 h-10 bg-[#48c9a6]/10 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-[#48c9a6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
              </svg>
            </div>
            <span className="text-xl font-bold text-gray-900">본인인증</span>
          </div>

          {/* 안내 문구 */}
          <div className="bg-[#48c9a6]/5 border border-[#48c9a6]/30 rounded-lg p-6 mb-8">
            <p className="text-sm text-gray-700 mb-2">
              아래의 <span className="font-semibold text-[#48c9a6]">안심본인 휴대폰인증</span> 또는 <span className="font-semibold text-[#48c9a6]">I-PIN</span> 중 하나를 선택하여 개인 인증 후 진행하시기 바랍니다.
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-semibold">안심본인 휴대폰인증</span> 및 <span className="font-semibold">I-PIN</span> 절차는 개인정보를 위해 인터넷상에서 주민번호 대신 이용할 수 있는 본인확인 서비스입니다.
            </p>
          </div>

          {/* 본인인증 버튼 */}
          <div className="max-w-3xl mx-auto">
            {!verified ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* I-PIN 인증 (비활성화) */}
                <button
                  disabled
                  className="flex flex-col items-center p-8 rounded-xl border-2 border-gray-200 bg-gray-50 cursor-not-allowed opacity-60"
                >
                  <div className="w-20 h-20 bg-gray-300 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-10 h-10 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                  </div>
                  <div className="text-lg font-bold text-gray-500 mb-2">아이핀 인증</div>
                  <div className="text-sm text-gray-400">(준비 중)</div>
                </button>

                {/* 휴대폰 본인인증 */}
                <button
                  onClick={handleVerify}
                  disabled={loading}
                  className="flex flex-col items-center p-8 rounded-xl border-2 border-green-200 hover:border-green-500 hover:shadow-xl bg-gradient-to-br from-green-50 to-white transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="text-lg font-bold text-gray-900 mb-2">
                    {loading ? '인증 처리 중...' : '휴대폰 본인인증'}
                  </div>
                  <div className="text-sm text-gray-600">
                    {loading ? '잠시만 기다려주세요' : '(테스트 버전)'}
                  </div>
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center p-12 rounded-xl border-2 border-green-500 bg-green-50">
                <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="text-xl font-bold text-green-700 mb-2">본인인증이 완료되었습니다</div>
                <div className="text-sm text-green-600">잠시 후 다음 단계로 이동합니다...</div>
              </div>
            )}
          </div>

          {/* 에러 메시지 */}
          {error && (
            <div className="mt-6 rounded-lg bg-red-50 border border-red-200 p-4">
              <p className="text-sm text-red-800 whitespace-pre-line">{error}</p>
            </div>
          )}

          {/* 개발 모드 안내 */}
          <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-xs text-yellow-800">
              ⚠️ <strong>개발 모드:</strong> 실제 PG 연동 전 테스트 버전입니다. 휴대폰 본인인증 버튼을 클릭하면 자동으로 테스트 본인인증 데이터가 생성됩니다.
            </p>
          </div>

          {/* 이전 단계 버튼 */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <button
              onClick={() => navigate('/signup')}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              ← 이전 단계로 돌아가기
            </button>
          </div>
        </div>
      </div>
      </div>

      <Footer />
    </div>
  );
};

export default SignupVerify;
